import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import { GridCellProps, GridItemChangeEvent } from "@progress/kendo-react-grid";

import CustomButton from "ui/Button";
import { CLASSNAME, COLOR } from "utils/constant";
import { Package } from "interface/common";

import Info from "ui/Info";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { SalesNoteLine } from "interface/salesNote";
import {
	deleteSalesNoteLine,
	getSalesNoteLineViewApi,
	salesNoteProductConditionFilterApi,
	saveSalesNoteLine,
	updateSalesNoteProductConditionFilterApi,
} from "api/salesNote";
import NoteModal from "../utils/NoteModal";
import { useInfoStore } from "store/error";
import { useToastStore } from "store/toast";
import { stepTwoGridColumns } from "../utils/StepTwoGridColumns";
import CustomGrid from "ui/grid/CustomGrid";
import { CustomCheckbox } from "ui/Input";
import { stepTwoRequirementCheck } from "../utils";
import StepTwoMobileList from "./StepTwoMobileList";
import { useUserStore } from "store/user";
import CustomModal from "ui/modal/Modal";

interface Form extends SalesNoteLine {
	id: number;
	inEdit: boolean;
	onfocus?: boolean;
	focusedField?: string;
}
export const FormStepTwo: FC<{
	form: Form[];
	setForm: React.Dispatch<React.SetStateAction<Form[]>>;
	showRequirement: boolean;
	setMeetRequirement: (meetRequirement: boolean) => void;
	addLineRef?: React.MutableRefObject<(() => void) | null>;
}> = ({ form, setForm, showRequirement, setMeetRequirement, addLineRef }) => {
	const { t } = useTranslation();
	const { id } = useParams();

	const [showDeleteLine, setShowDeleteLine] = useState(false);
	const [showNoteModal, setShowNoteModal] = useState(false);

	const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 1600);
	const [isNarrowView, setIsNarrowView] = useState(() => window.innerWidth < 1688 && window.innerWidth > 767);


	const [productConditionFilter, setProductConditionFilter] = useState<
		{ value: boolean; name: string; displayName: string }[]
	>([]);
	const modalDataRef = useRef<any>();
		const lastChangedPackage = useRef<Package>();
	const deletingLineRef = useRef<Form>();

	const { setError } = useInfoStore();
	const { showError } = useToastStore();
	const focusRef = useRef<{
		columnIndex: number;
		focusedField: string;
		rowIndex: number;
		id: number;
	}>();
	const bottomRef = useRef<HTMLDivElement>(null);
	const prevFormLengthRef = useRef<number>(form.length);

	const getData = useCallback(() => {
		if (!id) return;
		getSalesNoteLineViewApi(id as any)
			.then((response) => {
				setForm(response.data.map((line) => ({ ...line, inEdit: true })));
			})
			.catch((err) => {
				showError(`${t("Failed")}  ${t("To")} ${t("Save")}`, {
					duration: 3000,
				});
			});
	}, [id, setForm, showError, productConditionFilter]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 1600);
			setIsNarrowView(window.innerWidth < 1688 && window.innerWidth > 767);
		}
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		getData();
		salesNoteProductConditionFilterApi()
			.then((data) => {
				setProductConditionFilter(data);
			})
			.catch((err) => {
				console.log("failed to get product condition filter", err);
			});
	}, [id]);

	const { isAdmin } = useUserStore();

	useEffect(() => {
		setMeetRequirement(stepTwoRequirementCheck(form, isAdmin));
	}, [form, isAdmin]);

	const updateNoteLine = useCallback(
		(_form: Form, isOnSelect: boolean = false) => {
			if (!_form?.id) {
				_form.packagingCode = lastChangedPackage.current?.code as any;
				_form.noteHeadId = id as any;
			}

			saveSalesNoteLine({ ..._form })
				.then((data) => {
					setForm((prev) => {
						const existingLine = _form.id
							? prev.find((item: Form) => item.id === _form.id)
							: undefined;
						const savedLineId = Number(
							data.data?.id ?? _form.id ?? existingLine?.id ?? 0,
						);
						const line = {
							...existingLine,
							..._form,
							...data.data,
							product:
								data.data?.product ?? _form.product ?? existingLine?.product ?? "",
							packaging:
								data.data?.packaging ??
								_form.packaging ??
								existingLine?.packaging ??
								"",
							piecesMandatory:
								data.data?.piecesMandatory ??
								_form.piecesMandatory ??
								existingLine?.piecesMandatory ??
								null,
							focusedField:
								savedLineId === focusRef.current?.id
									? focusRef.current?.focusedField
									: undefined,
							onfocus: savedLineId === focusRef.current?.id,
							inEdit: true,
						} as Form;

						if (!_form.id) {
							return [...prev, line];
						}

						return prev.map((item: Form) => {
							if (item.id === line.id) {
								return line;
							}

							if (item.id === focusRef.current?.id) {
								return {
									...item,
									focusedField: focusRef.current.focusedField,
									onfocus: true,
								};
							}

							return { ...item, focusedField: undefined, onfocus: false };
						});
					});
					//}
					//  setForm(form.map((data) => (data.id === _form.id ? _form : data)));
				})
				.catch((err) => {
					console.log(err);
				});
		},
		[id, setForm],
	);

	const handleOnChangeItem = (e: GridItemChangeEvent) => {
		if (e.field) {
			if (e.dataItem[e.field] !== e.value) {
				e.dataItem[e.field] = e.value;
				if (
					e.field === "grossLiveWeightKg" &&
					(e.dataItem["netLiveWeightKg"] == null ||
						e.dataItem["netLiveWeightKg"] === 0)
				)
				{
					e.dataItem["netLiveWeightKg"] = e.value;
				}
				updateNoteLine(e.dataItem);

				// updateNoteLine(e.dataItem);
			}
		}
	};

	const startDeletingLine = useCallback((props: GridCellProps) => {
		deletingLineRef.current = props.dataItem;
		setShowDeleteLine(true);
	}, []);

	const startDeletingLineMobile = (line: Form) => {
		deletingLineRef.current = line;
		setShowDeleteLine(true);
	};

	const deleteLine = () => {
		if (!deletingLineRef.current) return;
		deleteSalesNoteLine(deletingLineRef.current.id)
			.then((response) => {
				if (response.returnIsOK) {
					setForm(
						form.filter(
							(item) => item.id !== (deletingLineRef.current?.id as any),
						),
					);
				}
				setShowDeleteLine(!showDeleteLine);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};
	const meetRequirement = (data: any, field: string) => {
		let isPassed = false;
		if (field === "numberOfPieces") {
			if (!data.piecesMandatory || data.piecesMandatory === 0) {
				return true;
			} else if (
				data.piecesMandatory === 1 &&
				(data[field] || data[field] > 0)
			) {
				return true;
			} else {
				return false;
			}
		}
		if (
			data[field] != null &&
			data[field] !== "null" &&
			data[field] !== "" &&
			data[field] !== undefined &&
			data[field] !== "undefined" &&
			data[field] !== "0" &&
			data[field] !== 0
		) {
			isPassed = true;
		}
		return isPassed;
	};

	const handleProductConditionChange = (name: string, value: boolean) => {
		const updated = productConditionFilter.map((item) =>
			item.name === name ? { ...item, value } : item,
		);

		const getValue = (key: string) =>
			updated.find((x) => x.name === key)?.value ?? false;

		updateSalesNoteProductConditionFilterApi({
			fresh: getValue("Fresh"),
			frozen: getValue("Frozen"),
			filet: getValue("Filet"),
			user: getValue("User"),
		})
			.catch((err) => {
				console.log("failed to update product condition filter", err);
			})
			.then(() => {
				setProductConditionFilter(updated);
			});
	};

	useEffect(() => {
		if (addLineRef) {
			addLineRef.current = () => updateNoteLine(Object());
		}
	});

	useEffect(() => {
		if (form.length > prevFormLengthRef.current) {
			bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
		}
		prevFormLengthRef.current = form.length;
	}, [form.length]);

	return (
		<div style={{ width: "100%", maxWidth: "100%" }}>
			<div className="sales-note-condition-filters" style={{ display: "flex", gap: 10, marginBottom: "1rem" }}>
				{productConditionFilter.map((condition) => (
					<div key={condition.name} className="sales-note-condition-filter-item">
						<CustomCheckbox
							value={condition.value}
							onChange={(value) =>
								handleProductConditionChange(condition.name, value)
							}
							label={t(condition.displayName)}
							containerStyle={{ width: "100%", justifyContent: "space-between" }}
						/>
					</div>
				))}
			</div>
			{showNoteModal && (
				<NoteModal
					showModal={showNoteModal}
					setShowModal={() => setShowNoteModal(!showNoteModal)}
					gridProps={modalDataRef.current}
				/>
			)}

			{isMobileView ? (
				<StepTwoMobileList
					form={form}
					isNarrowView={ isNarrowView }
					onUpdateLine={(line) => updateNoteLine(line, false)}
					onSelectPackage={(line) => {
						lastChangedPackage.current = { 
							code: line.packagingCode,
							displayName: "",
							name: line.packaging,
						};
						updateNoteLine(line, true);
					}}
					onOpenNote={(line) => {
						modalDataRef.current = { dataItem: line };
						setShowNoteModal(true);
					}}
					onDelete={startDeletingLineMobile}
					hasMeetRequirement={meetRequirement}
					showRequirementMetError={showRequirement}
				/>
			) : (
				<CustomGrid
					style={{ width: "100%" }}
					gridColumns={stepTwoGridColumns({
						update: (data) => updateNoteLine(data, true),
						hasMeetRequirement: meetRequirement,
						showError: showRequirement,
						isNarrowView: true,
						onSelectPackage: (data) => {
							lastChangedPackage.current = {
								code: data.packagingCode,
								displayName: "",
								name: data.packaging,
							};
							updateNoteLine(data, true);
						},

						openNoteModal: (props) => {
							modalDataRef.current = props;
							setShowNoteModal(!showNoteModal);
						},
						startDeletingLine: startDeletingLine,
						data: form || [],
						onChange: (data) => {
							handleOnChangeItem(data);
						},

						onFocus(data) {
							if (focusRef.current) {
								if (
									focusRef.current.focusedField === data.field &&
									focusRef.current.rowIndex === data.rowIndex
								) {
									return;
								}
							}
							focusRef.current = {
								columnIndex: data.columnIndex,
								focusedField: data.field,
								rowIndex: data.rowIndex,
								id: data.dataItem.id,
							};
							setForm((prev) => {
								return prev.map((item) =>
									item.id === data.dataItem.id
										? {
												...item,
												onfocus: true,
												focusedField: data.field,
											}
										: { ...item, onfocus: false, focusedField: undefined },
								);
							});
						},
					})}
					data={form || []}
					className={CLASSNAME.TableNumeric + " " + CLASSNAME.HideOverFlow}
					edit={{ id: true }}
				/>
			)}
			{/*<div style={{ marginTop: "" }}>*/}
			{/*	<CustomButton*/}
			{/*		label={t("NewLine") + " +"}*/}
			{/*		onClick={() => {*/}
			{/*			updateNoteLine(Object());*/}
			{/*		}}*/}
			{/*		bgColor={COLOR.Green}*/}
			{/*		color="white"*/}
			{/*		btnStyle={isMobileView ? { width: "100%" } : undefined}*/}
			{/*	/>*/}
			{/*</div>*/}

			<div ref={bottomRef} />

			{showDeleteLine && (
				<CustomModal
					title={t("DeleteLine")}
					onClose={() => setShowDeleteLine(false)}
					actions={[
						{
							label: t("Cancel"),
							onClick: () => setShowDeleteLine(false),
							bgColor: COLOR.Green,
							color: "white",
						},
						{
							label: t("Yes"),
							onClick: deleteLine,
							bgColor: COLOR.StatusRed,
							color: "white",
						},
					]}
				>
					<p style={{ padding: "16px 0" }}>{t("DeleteLineConfirmation")}</p>
				</CustomModal>
			)}
		</div>
	);
};

export default FormStepTwo;
